import { Accordion, AccordionTab } from "primereact/accordion"
import { Badge } from "primereact/badge"
import { Button } from "primereact/button"
import { classNames } from "primereact/utils"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { setCustomMapAppliedFilters, setCustomMapFiltersState } from "../../../../../stateManagement/reducers/custom/CustomMapSlice"
import { useAppSelector } from "../../../../../stateManagement/hooks/redux"
import { getItemWebStorage, setItemWebStorage } from "../../../../../features/webStorageSaver"
import { authorizeToCustomRest } from "../../../../../features/customAuth/customAuth"
import CustomFilterInput from "./customFilters/CustomFilterInput"
import CustomFilterListCheckbox from "./customFilters/CustomFilterListCheckbox"
import CustomFilterTreeCheckbox from "./customFilters/customFilterTreeCheckbox/CustomFilterTreeCheckbox"
import CustomFilterCalendar from "./customFilters/customFilterCalendar/CustomFilterCalendar"
import { configureAndSetCustomMapFilters } from "./customFilters/customFiltersFunctions"
import { configureTreeCheckboxNodes } from "./customFilters/customFilterTreeCheckbox/customFilterTreeCheckboxFunctions"

const filtersIcon = require('../../../../../assets/images/icons/filters-icon.svg').default

export default function CustomMapOverlay({ filterOptions, mapName }: any) {
    const dispatch = useDispatch()
    const [filterMenuIsShown, setFilterMenuIsShown] = useState<boolean>(false)
    const [activeFiltersTabs, setActiveFiltersTabs] = useState<any>()
    const listValuesFromSource = getItemWebStorage(`customMapFiltersListValues-${mapName}`) ?? {}
    const treeValuesFromSource = getItemWebStorage(`customMapFiltersTreeValues-${mapName}`) ?? {}
    const restAuthToken = useAppSelector(state => state.customAuth.customAuthToken)

    const filtersState = useAppSelector(state => state.customMap.customMapFiltersState)
    const appliedFilters = useAppSelector(state => state.customMap.customMapAppliedFilters)

    useEffect(() => {
        const savedFiltersState = getItemWebStorage(`customMapFiltersState-${mapName}`)
        const savedAppliedFilters = getItemWebStorage(`customMapAppliedFilters-${mapName}`)
        const initFiltersState = savedFiltersState ?? configureInitialFiltersState()
        const initAppliedFilters = savedAppliedFilters ?? configureInitialAppliedFilters()
        dispatch(setCustomMapFiltersState(initFiltersState))
        dispatch(setCustomMapAppliedFilters(initAppliedFilters))
    }, [])

    function configureInitialFiltersState(): any {
        let filtersState = {}
        filterOptions.map((filterOption: any) => {
            let { filterCaption, filterType, fieldName, valueType, filterForm, listValues, listValuesSource, treeValues, treeValuesSource, hasSearch, presetValues, nullable } = filterOption
            let filterState: any = {
                filterForm,
                filterCaption,
                filterType,
                valueType,
            }
            let ignoreFilterValue = { caption: 'Не учитывать', value: `ignore-${fieldName}`, checked: true }
            let nullFilterValue = { caption: 'Не указан', value: `null-${fieldName}`, checked: false }
            if (filterForm === "list-checkbox") {
                if (listValues) {
                    listValues = listValues.map((listValue: any) => { return { ...listValue, ...{ checked: false } } })
                }
                filterState = {
                    ...filterState,
                    listValues,
                    ignoreFilterValue,
                    hasSearch,
                    searchValue: '',
                    listValuesSource,
                    nullable
                }
                if (nullable) {
                    filterState = { ...filterState, nullFilterValue }
                }

            }
            if (filterForm === "tree-checkbox") {
                if (treeValues) {
                    treeValues = configureTreeCheckboxNodes(treeValues)
                }
                filterState = {
                    ...filterState,
                    treeValues,
                    selectedTreeValues: [],
                    ignoreFilterValue,
                    nullFilterValue,
                    hasSearch,
                    searchValue: '',
                    treeValuesSource
                }
            }
            if (filterForm === "input") {
                filterState = {
                    ...filterState,
                    ignoreFilterValue,
                    nullFilterValue,
                    value: null
                }
            }
            if (filterForm === "calendar") {
                filterState = {
                    ...filterState,
                    ignoreFilterValue,
                    nullFilterValue,
                    presetValues
                }
                if (filterType === "between") {
                    filterState = { ...filterState, minValue: null, maxValue: null }
                }
                if (filterType === "from") {
                    filterState = { ...filterState, value: null }
                }
            }
            Object.assign(filtersState, {
                [fieldName]: filterState
            })
        })
        return filtersState
    }

    function configureInitialAppliedFilters(): any {
        let appliedFilters: any = {}
        filterOptions.map((filterOption: any) => {
            let { filterType, fieldName, valueType, filterForm } = filterOption
            Object.assign(appliedFilters, {
                [fieldName]: {
                    filterForm,
                    filterType,
                    valueType
                }
            })
            if (filterForm === "input") {
                appliedFilters[fieldName].value = null
                appliedFilters[fieldName][`ignore-${fieldName}`] = true
            }
            if (filterForm === "list-checkbox") {
                appliedFilters[fieldName].listValues = { [`ignore-${fieldName}`]: true }
            }
            if (filterForm === "tree-checkbox") {
                appliedFilters[fieldName].treeValues = { [`ignore-${fieldName}`]: true }
            }
            if (filterForm === "calendar") {
                if (filterType === "between") {
                    appliedFilters[fieldName].minValue = null
                    appliedFilters[fieldName].maxValue = null
                }
                if (filterType === "from") {
                    appliedFilters[fieldName].value = null
                }
                appliedFilters[fieldName][`ignore-${fieldName}`] = true
            }
        })
        return appliedFilters
    }

    useEffect(() => {
        const restAuthRequestOptions = getItemWebStorage('currentConfig')?.mapAdditionalItems?.find((item: any) => item?.objectType === mapName)?.restAuthRequestOptions
        if (restAuthRequestOptions && !restAuthToken) {
            authorizeToCustomRest(restAuthRequestOptions)
        }
    }, [mapName])

    function handleClearAllFilters() {
        let filtersInitState = configureInitialFiltersState()
        Object.entries(filtersInitState).map(([fieldName, filterState]: any) => {
            const { listValuesSource, treeValuesSource } = filterState
            if (listValuesSource) {
                let listValues = listValuesFromSource[fieldName]
                filtersInitState[fieldName].listValues = listValues
            }
            if (treeValuesSource) {
                let treeValues = treeValuesFromSource[fieldName]
                filtersInitState[fieldName].treeValues = treeValues
            }
        })
        dispatch(setCustomMapFiltersState(filtersInitState))
        setItemWebStorage(`customMapFiltersState-${mapName}`, filtersInitState)
        const initAppliedFilters = configureInitialAppliedFilters()
        dispatch(setCustomMapAppliedFilters(initAppliedFilters))
        setItemWebStorage(`customMapAppliedFilters-${mapName}`, initAppliedFilters)
    }

    function clearSingleFilter(event: any, fieldName: string) {
        event.stopPropagation()
        event.preventDefault()
        let newFiltersState = structuredClone(filtersState)
        const initFilterState = configureInitialFiltersState()[fieldName]
        if (initFilterState.listValuesSource) {
            initFilterState.listValues = listValuesFromSource[fieldName]
        }
        if (initFilterState.treeValuesSource) {
            initFilterState.treeValues = treeValuesFromSource[fieldName]
        }
        newFiltersState[fieldName] = structuredClone(initFilterState)
        let newAppliedFilters = structuredClone(appliedFilters)
        const initAppliedFilter = configureInitialAppliedFilters()[fieldName]
        newAppliedFilters[fieldName] = structuredClone(initAppliedFilter)
        dispatch(setCustomMapFiltersState(newFiltersState))
        setItemWebStorage(`customMapFiltersState-${mapName}`, newFiltersState)
        dispatch(setCustomMapAppliedFilters(newAppliedFilters))
        setItemWebStorage(`customMapAppliedFilters-${mapName}`, newAppliedFilters)
    }

    // функция для подсчета всех активных фильтров
    function countAllAppliedFilters() {
        let allAppliedFiltersNumber = 0
        appliedFilters && Object.entries(appliedFilters).map(([fieldName, appliedFilter]: any) => {
            const { filterForm, value, listValues, treeValues, minValue, maxValue, [`ignore-${fieldName}`]: ignoreFilter } = appliedFilter
            if (filterForm === 'input' && !ignoreFilter) {
                allAppliedFiltersNumber++
            }
            if (listValues && !listValues[`ignore-${fieldName}`]) {
                allAppliedFiltersNumber += countAppliedFilterForListOrTreeCheckbox(listValues, fieldName)
            }
            if (treeValues && !treeValues[`ignore-${fieldName}`]) {
                allAppliedFiltersNumber += countAppliedFilterForListOrTreeCheckbox(treeValues, fieldName)
            }
            if (filterForm === 'calendar' && !ignoreFilter && (minValue && maxValue) || value) {
                allAppliedFiltersNumber++
            }
        })
        return allAppliedFiltersNumber
    }

    // функция для подсчета всех активных полей для фильтров tree-checkbox и list-checkbox
    function countAppliedFilterForListOrTreeCheckbox(filterValues: any, fieldName: any) {
        return Object.entries(filterValues).filter(([value, checked]: any) => (value != `ignore-${fieldName}` && checked)).length
    }

    // Функция для получения шаблона заголовка аккордеона
    function getAccordionTabHeaderTemplate(caption: string, fieldName: string, filterForm: any, filterType: any) {
        let showClearFiltersButton = false
        let numberOfActiveFilters
        const appliedFiltersForField = appliedFilters?.[fieldName]
        if (appliedFiltersForField) {
            const ignoreFilter = appliedFiltersForField?.[`ignore-${fieldName}`]
            const { listValues, treeValues, minValue, maxValue, value } = appliedFiltersForField
            if (filterForm === 'list-checkbox' && listValues) {
                showClearFiltersButton = !listValues?.[`ignore-${fieldName}`]
                numberOfActiveFilters = countAppliedFilterForListOrTreeCheckbox(listValues, fieldName)
            }
            if (filterForm === 'tree-checkbox' && treeValues) {
                showClearFiltersButton = !treeValues?.[`ignore-${fieldName}`]
                numberOfActiveFilters = countAppliedFilterForListOrTreeCheckbox(treeValues, fieldName)
            }
            if (filterForm === "input") {
                showClearFiltersButton = !ignoreFilter
                numberOfActiveFilters = 1
            }
            if (filterForm === "calendar") {
                if (filterType === "between" && minValue && maxValue) {
                    showClearFiltersButton = !ignoreFilter
                    numberOfActiveFilters = 1
                }
                if (filterType === "from" && value) {
                    showClearFiltersButton = !ignoreFilter
                    numberOfActiveFilters = 1
                }
            }
        }
        return (
            <div className="map-overlay-filter-header-div">
                <span className="map-overlay-filter-header-name p-overlay-badge">
                    {caption}
                    {showClearFiltersButton &&
                        <Badge value={numberOfActiveFilters}></Badge>
                    }
                </span>
                {showClearFiltersButton &&
                    <Button onClick={(event) => clearSingleFilter(event, fieldName)} className="map-overlay-filter-header-clear-but" severity="secondary" icon="pi pi-filter-slash" rounded outlined />
                }
            </div>
        )
    }

    let mapOverlayClassName = classNames({
        "map-overlay": true,
        "with-filters": true,
        "filters-div-opened": filterMenuIsShown
    })

    useEffect(() => {
        if (appliedFilters) {
            configureAndSetCustomMapFilters(appliedFilters)
        }
    }, [appliedFilters])

    return (
        <div className={mapOverlayClassName}>
            <div className="buttons-div">
                {filterMenuIsShown &&
                    <Button onClick={handleClearAllFilters} className="clear-all-filters">Сбросить все фильтры</Button>
                }
                <div className="filters-button-div p-overlay-badge" onClick={() => { setFilterMenuIsShown(!filterMenuIsShown) }}>
                    <div className="filter-button-image-div">
                        <img src={filtersIcon} />
                    </div>
                    <span>Фильтры</span>
                    {(!filterMenuIsShown && countAllAppliedFilters() != undefined) &&
                        <Badge value={countAllAppliedFilters()}></Badge>
                    }
                </div>
            </div>
            {filterMenuIsShown &&
                <div className="filters-div">
                    <Accordion multiple activeIndex={activeFiltersTabs} onTabChange={(event) => setActiveFiltersTabs(event.index)}>
                        {Object.entries(filtersState).map(([fieldName, filterState]: any) => {
                            const { filterCaption, filterForm, filterType } = filterState
                            return (
                                <AccordionTab header={getAccordionTabHeaderTemplate(filterCaption, fieldName, filterForm, filterType)}>
                                    {filterForm === "input" &&
                                        <CustomFilterInput fieldName={fieldName} objectTypeName={mapName} />
                                    }
                                    {filterForm === "list-checkbox" &&
                                        <CustomFilterListCheckbox fieldName={fieldName} objectTypeName={mapName} />
                                    }
                                    {filterForm === "tree-checkbox" &&
                                        <CustomFilterTreeCheckbox fieldName={fieldName} objectTypeName={mapName} />
                                    }
                                    {filterForm === "calendar" &&
                                        <CustomFilterCalendar fieldName={fieldName} objectTypeName={mapName} />
                                    }
                                </AccordionTab>
                            )
                        })}
                    </Accordion>
                </div>
            }
        </div>
    )
}
